import { Menu } from '@base-ui/react/menu';
import type { CSSObject, Theme } from '@emotion/react';
import styled from '@emotion/styled';

import {
  DISABLED_EXCLUSIONS,
  DISABLED_SELECTORS,
  focusRing,
  hoverable,
  pressable,
} from '../../interaction-states';
import { forHover } from '../../responsive';
import { zIndex } from '../../z-index';
import Button from '../Button';

export const TriggerButton = styled('button')(({ theme }) => ({
  ...hoverable(theme, 'text'),
  ...pressable(theme, 'text'),
  ...focusRing(theme),
  height: 32,
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing[16],
  padding: 0,
  borderRadius: theme.radius[8],
  backgroundColor: 'transparent',
  border: 'none',
  cursor: 'pointer',
  color: theme.colors.text.secondary,
  minWidth: 0,

  '&[data-popup-open]': {
    color: theme.colors.text.primary,
  },

  '&[data-popup-open] .ds-chevron': {
    transform: 'rotate(180deg)',
  },

  [DISABLED_SELECTORS]: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
}));

export const TriggerContent = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing[8],
  flex: 1,
  minWidth: 0,
}));

export const UserInfo = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
});

export const TriggerUserInfo = styled(UserInfo)({
  alignItems: 'flex-start',
  overflow: 'hidden',
  flexShrink: 0,
  width: 112,
});

export const BadgeRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 3,
});

export const BadgeBar = styled('div')<{ $color: string }>(({ $color }) => ({
  width: 2,
  height: 8,
  backgroundColor: $color,
  flexShrink: 0,
}));

export const TriggerEnd = styled('div')({
  height: 20,
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  flexShrink: 0,
});

export const UserIconWrapper = styled('div')(({ theme }) => ({
  width: 32,
  height: 32,
  borderRadius: theme.radius.full,
  backgroundColor: theme.colors.background.overlay,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  overflow: 'hidden',

  img: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },

  svg: {
    width: 16,
    height: 16,
  },
}));

export const ChevronWrapper = styled('div')({
  width: 16,
  height: 16,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  transition: 'transform 0.2s ease',

  svg: {
    width: 12,
    height: 12,
  },
});

export const DropdownPositioner = styled(Menu.Positioner)({
  zIndex: zIndex.dropdown,
});

export const DropdownCard = styled('div')(({ theme }) => ({
  width: 304,
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[4],
  padding: theme.spacing[4],
  backgroundColor: theme.colors.background.raised,
  border: `1px solid ${theme.colors.border.default}`,
  borderRadius: theme.radius[8],
  outline: 'none',
  transformOrigin: 'top right',
  transition: 'opacity 150ms ease, transform 150ms cubic-bezier(0.4, 0, 0.2, 1)',

  '&[data-starting-style], &[data-ending-style]': {
    opacity: 0,
    transform: 'scale(0.96) translateY(-2px)',
  },
}));

const dropdownRow = (theme: Theme): CSSObject => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing[8],
  width: '100%',
  minHeight: 40,
  boxSizing: 'border-box',
  textAlign: 'left',
  padding: theme.spacing[8],
  borderRadius: theme.radius[8],
  background: 'none',
  border: 'none',
  transition: theme.motion.hover,

  [forHover]: {
    [`&:hover${DISABLED_EXCLUSIONS}`]: {
      backgroundColor: theme.colors.background.overlay,
    },
  },

  [`&:active${DISABLED_EXCLUSIONS}`]: {
    backgroundColor: theme.colors.neutral.active,
  },

  ...focusRing(theme),

  [DISABLED_SELECTORS]: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
});

export const DropdownDivider = styled('hr')(({ theme }) => ({
  border: 'none',
  borderTop: `1px solid ${theme.colors.border.default}`,
  margin: `${theme.spacing[4]}px 0`,
  width: '100%',
}));

export const UserHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing[8],
}));

export const SectionLabel = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing[8],
}));

export const TooltipRow = styled('span')({
  display: 'block',
  width: '100%',
});

export const RoleButton = styled('button')(({ theme }) => ({
  ...dropdownRow(theme),
  cursor: 'pointer',
  color: theme.colors.text.primary,

  '& > svg': {
    width: 20,
    height: 20,
    flexShrink: 0,
    color: theme.colors.icon.brand,
  },
}));

export const EntitiesScrollContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  maxHeight: 180,
  overflowY: 'auto',

  '&::-webkit-scrollbar': {
    display: 'none',
  },
});

export const DropdownSection = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
});

export const DropdownCloseRow = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
});

export const AddEntityButton = styled(Button)({
  justifyContent: 'flex-start',
});

export const StatusIconBox = styled('span')({
  display: 'inline-flex',
  width: 16,
  height: 16,
  flexShrink: 0,

  svg: {
    width: '100%',
    height: '100%',
  },
});

export const RoleInfo = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  flex: 1,
  minWidth: 0,
  alignItems: 'center',
  gap: theme.spacing[4],
  overflow: 'hidden',
}));

export const StackedRoleInfo = styled(RoleInfo)(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: theme.spacing[2],

  '& > *': {
    maxWidth: '100%',
  },
}));

export const ManageAccountLink = styled('a')(({ theme }) => ({
  ...dropdownRow(theme),
  color: theme.colors.text.primary,
  textDecoration: 'none',

  svg: {
    width: 20,
    height: 20,
    flexShrink: 0,
  },
}));

export const LogoutButton = styled('button')(({ theme }) => ({
  ...dropdownRow(theme),
  cursor: 'pointer',
  color: theme.colors.text.error,

  svg: {
    width: 20,
    height: 20,
    flexShrink: 0,
  },
}));

export const DropdownCloseButton = styled('button')(({ theme }) => ({
  ...hoverable(theme),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 4,
  background: 'none',
  border: 'none',
  borderRadius: theme.radius[4],
  cursor: 'pointer',
  color: 'inherit',
}));
